/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, SelectOption } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import type { ComboBoxItem } from 'bundles/employability-common/components/ComboBox';
import ComboBox from 'bundles/employability-common/components/ComboBox';
import { searchMatcher } from 'bundles/employability-common/components/utils';

import _t from 'i18n!nls/account-profile';

const styles = {
  selectOption: css`
    width: 100%;
  `,
  headerButton: () => css`
    margin-left: var(--cds-spacing-200);
    margin-bottom: calc(var(--cds-spacing-150) * -1);
    justify-content: flex-start;
    padding-left: 0;
  `,
  header: () => css`
    padding-right: var(--cds-spacing-300);
  `,
};

type Props = {
  options: { label: string; value: string }[];
  onSelect: (value: string) => void;
  placeholder: string;
  onSearchChange?: (query: string) => void;
  customComboBoxItems?: Array<ComboBoxItem>;
  errorMessage?: string;
  'data-e2e'?: string;
};

const SelectWithCreateNewOption: React.VFC<Props> = ({
  options,
  onSelect,
  placeholder,
  onSearchChange,
  customComboBoxItems = [],
  errorMessage,
  'data-e2e': e2e,
}) => {
  const formatComboBoxItem = (label: string, value: string) => {
    return {
      key: value,
      label,
      onClick: (closePopover?: () => void) => {
        onSelect(value);
        closePopover?.();
      },
      match: (term?: string) => searchMatcher({ label }, term),
      content: () => (
        <SelectOption value={value} css={styles.selectOption}>
          {label}
        </SelectOption>
      ),
    };
  };
  const [query, setQuery] = useState<string>('');
  const [comboBoxItems, setComboBoxItems] = useState<Array<ComboBoxItem>>(
    options.map(({ label, value }) => formatComboBoxItem(label, value))
  );

  const createNewOption = (closePopOver: () => void) => {
    // Check to see if the option already exists
    const existingOption = [...comboBoxItems, ...customComboBoxItems].filter((item) => item.label === query)?.[0];

    if (!existingOption) {
      setComboBoxItems((prevComboBoxItems) => [...prevComboBoxItems, formatComboBoxItem(query, query)]);
    }

    onSelect(existingOption?.key || query);
    closePopOver();
  };

  const onSearch = (searchQuery?: string) => {
    setQuery(searchQuery ?? '');
    onSearchChange?.(searchQuery ?? '');
  };

  return (
    <ComboBox
      data-e2e={e2e}
      items={[...comboBoxItems, ...customComboBoxItems]}
      activePlaceholder={_t('Type to search')}
      inactivePlaceholder={placeholder}
      fullWidth={true}
      onSearch={onSearch}
      errorMessage={errorMessage}
      header={
        query
          ? (closePopOver: () => void) => (
              <div css={styles.header}>
                <Button
                  css={styles.headerButton}
                  variant="ghost"
                  iconPosition="before"
                  icon={<AddIcon />}
                  onClick={() => createNewOption(closePopOver)}
                  fullWidth
                >
                  <FormattedMessage message={_t('Create item "{searchText}"')} searchText={query} />
                </Button>
              </div>
            )
          : undefined
      }
    />
  );
};

export default SelectWithCreateNewOption;
